/**
 * Utility functions for handling sponsor data across components
 */

/**
 * Filters and validates sponsor data
 * @param {Array} sponsors - Array of sponsor objects
 * @returns {Array} Filtered array of valid sponsors
 */
export const getValidSponsors = (sponsors) => {
  if (!sponsors || sponsors.length === 0) return [];
  return sponsors.filter((sponsor) => sponsor && sponsor.sponsor);
};

/**
 * Gets sponsor names as an array of strings
 * @param {Array} sponsors - Array of sponsor objects
 * @returns {Array} Array of sponsor names
 */
export const getSponsorNames = (sponsors) => {
  const validSponsors = getValidSponsors(sponsors);
  return validSponsors.map((sponsor) => sponsor.sponsor);
};

/**
 * Creates a display string for sponsors with truncation
 * @param {Array} sponsors - Array of sponsor objects
 * @param {Object} options - Display options
 * @param {number} options.maxLength - Maximum length before truncation
 * @param {boolean} options.showCount - Whether to show count for multiple sponsors
 * @param {boolean} options.extraSmall - Whether this is for extra small display
 * @returns {string} Formatted sponsor display string
 */
export const getSponsorDisplayText = (sponsors, options = {}) => {
  const { maxLength = 25, showCount = true, extraSmall = false } = options;

  const validSponsors = getValidSponsors(sponsors);
  if (validSponsors.length === 0) return '';

  const sponsorNames = getSponsorNames(validSponsors);
  let displayText = sponsorNames.join(' & ');

  // Truncate if too long
  const actualMaxLength = extraSmall ? Math.min(maxLength, 15) : maxLength;
  if (displayText.length > actualMaxLength) {
    displayText = displayText.substring(0, actualMaxLength) + '...';
  }

  // If we have multiple sponsors and showCount is true, show first + count
  if (validSponsors.length > 1 && showCount && !extraSmall) {
    const firstSponsor = validSponsors[0];
    displayText = `${firstSponsor.sponsor} +${validSponsors.length - 1}`;
    if (displayText.length > actualMaxLength) {
      displayText = displayText.substring(0, actualMaxLength) + '...';
    }
  }

  return displayText;
};

/**
 * Creates a media session artist string with sponsors
 * @param {string} artistName - Main artist name
 * @param {string} setName - Set name
 * @param {Array} sponsors - Array of sponsor objects
 * @returns {string} Formatted string for media session
 */
export const getMediaSessionArtistString = (artistName, setName, sponsors) => {
  const sponsorNames = getSponsorNames(sponsors);
  const sponsorText =
    sponsorNames.length > 0 ? sponsorNames.join(' & ') : 'Unknown';
  return `${artistName} - ${setName} [${sponsorText}]`;
};

/**
 * Separates curators from labels
 * @param {Array} sponsors - Array of sponsor objects
 * @returns {Object} Object with curators and labels arrays
 */
export const separateSponsorsByType = (sponsors) => {
  const validSponsors = getValidSponsors(sponsors);
  return {
    curators: validSponsors.filter(
      (sponsor) => sponsor.sponsor_type !== 'label_meta'
    ),
    labels: validSponsors.filter(
      (sponsor) => sponsor.sponsor_type === 'label_meta'
    ),
  };
};
